import { AuthContext } from "@/auth/authcontext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLogin } from "@/hooks/api/use-auth";
import { FormEvent, useContext, useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";

interface ApiError {
  errors?: Record<string, string[]>;
  detail?: string;
  non_field_errors?: string[];
  message?: string;
  status?: number;
}

function LoginForm() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const login = useLogin();
  const { setUserDetails } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    login.mutate(formData, {
      onSuccess: (data) => {
        if (data.status === "success" && data.jwt) {
          localStorage.setItem("refreshToken", data.jwt.refresh);
          localStorage.setItem("accessToken", data.jwt.access);
          setUserDetails({
            access: data.jwt.access,
            refresh: data.jwt.refresh,
            user: data.jwt.user,
          });
          toast.success("Successfully logged in!");
          navigate("/");
        } else if (data.status === "otp_required" && data.tempOtpToken) {
          localStorage.setItem("tempOtpToken", data.tempOtpToken);
          navigate("/login/otp");
        }
      },
      onError: (error: ApiError) => {
        if (error.errors) {
          Object.entries(error.errors).forEach(([field, messages]) => {
            (messages as string[])?.forEach((message) => {
              toast.error(`${field}: ${message}`);
            });
          });
        } else if (error.detail) {
          toast.error(error.detail);
        } else if (error.non_field_errors) {
          error.non_field_errors.forEach((message) => {
            if (message === "Unable to log in with provided credentials.") {
              toast.error("Incorrect email or password");
            } else {
              toast.error(message);
            }
          });
        } else {
          toast.error(error.message || "Failed to login. Please try again.");
        }
      },
    });
  };

  return (
    <Card className="mx-auto max-w-sm">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Sign In</CardTitle>
        <CardDescription>
          Enter your email and password to sign in to your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                placeholder="m@example.com"
                required
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                required
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, password: e.target.value }))
                }
              />
            </div>
            <Button className="w-full" type="submit" disabled={login.isPending}>
              {login.isPending ? "Signing in..." : "Sign in"}
            </Button>
          </div>
        </form>
        <div className="mt-4 text-center text-sm">
          Don't have an account?{" "}
          <Link to="/signup" className="underline">
            Sign up
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export default function LoginPage() {
  return (
    <div className="container flex h-screen w-screen flex-col items-center justify-center">
      <LoginForm />
    </div>
  );
}
